import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import cartService from "../services/cartService";
import { useAuth } from "./AuthContext";
import { useTranslation } from "react-i18next";

// Create cart context
const CartContext = createContext();

/**
 * CartProvider component for managing cart state
 * @param {Object} props - Component props
 * @returns {JSX.Element} CartProvider component
 */
export const CartProvider = ({ children }) => {
  const { t: tCart } = useTranslation("cart");
  const { isAuthenticated, user } = useAuth();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [itemCount, setItemCount] = useState(0);

  // Get tax rate from Redux store for fallback
  const { taxRate } = useSelector((state) => state.tax);

  // Fetch cart data - Memoized with useCallback
  const fetchCart = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setLoading(true);
      setError(null);
      const cartData = await cartService.getCart();

      // Ensure tax rate is present in cart data
      if (!cartData.taxRate && taxRate?.rate) {
        cartData.taxRate = parseFloat(taxRate.rate);
      }

      setCart(cartData);
      setItemCount(cartData.items ? cartData.items.length : 0);
    } catch (err) {
      console.error("Error fetching cart:", err);
      setError(tCart("context.error.fetchFailed"));
      toast.error(tCart("context.error.fetchFailed"));
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, taxRate, tCart]); // Dependency: re-create if isAuthenticated or taxRate changes

  // Fetch cart when user is authenticated or fetchCart function reference changes
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchCart();
    } else {
      // Clear cart when user logs out
      setCart(null);
      setItemCount(0);
    }
  }, [isAuthenticated, user, fetchCart]); // Include fetchCart here

  // Notify other components when cart changes, especially when items are added/removed
  useEffect(() => {
    // Create a custom event that other components can listen for
    if (cart) {
      const cartChangeEvent = new CustomEvent("cart-updated", {
        detail: {
          itemCount: cart.items ? cart.items.length : 0,
          subtotal: cart.subtotal,
        },
      });
      window.dispatchEvent(cartChangeEvent);
    }
  }, [cart]);

  // Add item to cart - Memoized with useCallback
  const addToCart = useCallback(
    async (productId, quantity = 1, options = {}) => {
      if (!isAuthenticated) {
        toast.error(tCart("context.error.loginRequired"));
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Ensure options are properly structured for the backend
        const item = {
          productId,
          quantity,
          options: {},
        };

        // Handle variation products with special care
        if (options.variationId) {
          item.options = {
            variationId: options.variationId,
            useVariationStock: true,
            ...(options.variationName && {
              variationName: options.variationName,
            }),
            ...(options.attributes && { attributes: options.attributes }),
          };
        } else {
          // For regular products, just include any provided options
          item.options = { ...options };
        }

        console.log("Sending to backend:", item);
        const result = await cartService.addToCart(item);

        // Update cart state by re-fetching
        await fetchCart();

        toast.success(tCart("context.success.itemAdded"));
        return result;
      } catch (err) {
        console.error("Error adding to cart:", err);
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          tCart("context.error.addFailed");
        const availableStock = err.response?.data?.availableStock;
        let detailedError = errorMessage;
        if (availableStock !== undefined) {
          detailedError = tCart("stockInfo.onlyXAvailable", {
            stock: availableStock,
            inCart: 0,
          });
        }
        setError(detailedError);
        toast.error(detailedError);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [isAuthenticated, fetchCart, tCart]
  ); // Dependencies

  // Update cart item - Memoized with useCallback
  const updateCartItem = useCallback(
    async (itemId, quantity) => {
      if (!isAuthenticated) return;

      try {
        setLoading(true);
        const result = await cartService.updateCartItem(itemId, { quantity });
        await fetchCart();
        toast.success(tCart("context.success.itemUpdated"));
        return result;
      } catch (err) {
        console.error("Error updating cart:", err);
        if (err.availableStock !== undefined) {
          const stockErrorMsg = tCart("stockInfo.onlyXAvailable", {
            stock: err.availableStock,
            inCart: 0,
          });
          toast.error(stockErrorMsg);
        } else {
          setError(err.message || tCart("context.error.updateFailed"));
          toast.error(err.message || tCart("context.error.updateFailed"));
        }
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [isAuthenticated, fetchCart, tCart]
  ); // Dependencies

  // Remove item from cart - Memoized with useCallback
  const removeFromCart = useCallback(
    async (itemId) => {
      if (!isAuthenticated) return;

      try {
        setLoading(true);
        setError(null);
        const result = await cartService.removeFromCart(itemId);
        await fetchCart();
        toast.success(tCart("context.success.itemRemoved"));
        return result;
      } catch (err) {
        console.error("Error removing from cart:", err);
        const msg =
          err.response?.data?.message || tCart("context.error.removeFailed");
        setError(msg);
        toast.error(msg);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [isAuthenticated, fetchCart, tCart]
  ); // Dependencies

  // Clear cart - Memoized with useCallback
  const clearCart = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setLoading(true);
      setError(null);
      const result = await cartService.clearCart();
      // Update cart state locally immediately for better UX, backend confirms
      setCart((prevCart) => ({
        ...prevCart,
        items: [],
        subtotal: 0,
        total: 0,
      }));
      setItemCount(0);
      toast.success(tCart("context.success.cleared"));
      return result;
    } catch (err) {
      console.error("Error clearing cart:", err);
      const msg =
        err.response?.data?.message || tCart("context.error.clearFailed");
      setError(msg);
      toast.error(msg);
      // Re-fetch cart on error to ensure consistency
      await fetchCart();
      return null;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, fetchCart, tCart]); // Dependencies

  // Apply coupon to cart - Memoized with useCallback
  const applyCoupon = useCallback(
    async (couponCode) => {
      if (!isAuthenticated) {
        toast.error(tCart("context.error.loginRequired"));
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const updatedCart = await cartService.applyCoupon(couponCode);
        setCart(updatedCart); // Update local state
        setItemCount(updatedCart.items ? updatedCart.items.length : 0);
        toast.success(tCart("context.success.couponApplied"));
        return updatedCart;
      } catch (err) {
        console.error("Error applying coupon:", err);
        const msg = err.response?.data?.message || tCart("coupon.applyFailed");
        setError(msg);
        toast.error(msg);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [isAuthenticated, tCart]
  ); // Dependencies

  // Remove coupon from cart - Memoized with useCallback
  const removeCoupon = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setLoading(true);
      setError(null);
      const updatedCart = await cartService.removeCoupon();
      setCart(updatedCart); // Update local state
      setItemCount(updatedCart.items ? updatedCart.items.length : 0);
      toast.success(tCart("context.success.couponRemoved"));
      return updatedCart;
    } catch (err) {
      console.error("Error removing coupon:", err);
      const msg = err.response?.data?.message || tCart("coupon.removeFailed");
      setError(msg);
      toast.error(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, tCart]); // Dependencies

  // Calculate total items in cart - Memoized with useMemo
  const totalItems = useMemo(() => {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((total, item) => total + item.quantity, 0);
  }, [cart?.items]); // Dependency: cart.items array reference

  // Context value - Memoized with useMemo
  const value = useMemo(
    () => ({
      cart,
      loading,
      error,
      itemCount,
      totalItems, // Use the memoized value
      fetchCart,
      addToCart,
      updateCartItem,
      removeFromCart,
      clearCart,
      applyCoupon,
      removeCoupon,
    }),
    [
      cart,
      loading,
      error,
      itemCount,
      totalItems,
      fetchCart,
      addToCart,
      updateCartItem,
      removeFromCart,
      clearCart,
      applyCoupon,
      removeCoupon,
    ]
  ); // Dependencies include all state and memoized functions

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

/**
 * Custom hook for using cart context
 * @returns {Object} Cart context value
 */
export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

export default CartContext;
